/**
 * src/db/appState.ts — persisted UI state and the reusable tax profile (§9, §6.20).
 * Both are Zod-validated on read; invalid blobs fall back to defaults / null.
 */
import { taxProfileSchema, uiStateSchema } from '@/schema';
import type { TaxProfile, UiState } from '@/types';
import { getDb } from './index';

const UI_KEY = 'ui';
const TAX_PROFILE_KEY = 'taxProfile';

export async function getUiState(): Promise<UiState> {
  const db = await getDb();
  const entry = await db.get('appState', UI_KEY);
  const parsed = uiStateSchema.safeParse(entry?.value);
  return parsed.success ? parsed.data : uiStateSchema.parse({});
}

export async function setUiState(state: UiState): Promise<void> {
  const db = await getDb();
  await db.put('appState', { key: UI_KEY, value: state });
}

/** The saved tax profile, or null when unset/invalid (§6.20). */
export async function getTaxProfile(): Promise<TaxProfile | null> {
  const db = await getDb();
  const entry = await db.get('appState', TAX_PROFILE_KEY);
  if (!entry) return null;
  const parsed = taxProfileSchema.safeParse(entry.value);
  return parsed.success ? parsed.data : null;
}

export async function setTaxProfile(profile: TaxProfile): Promise<void> {
  const db = await getDb();
  await db.put('appState', { key: TAX_PROFILE_KEY, value: profile });
}

export async function clearTaxProfile(): Promise<void> {
  const db = await getDb();
  await db.delete('appState', TAX_PROFILE_KEY);
}
